import { Box, Typography } from '@mui/material';
import React from 'react';
import { Trans } from '@lingui/macro';

import { DASHBOARD_LIST_COLUMN_WIDTHS } from '../../utils/dashboardSortUtils';
import { ListHeaderWrapper } from '../../components/lists/ListHeaderWrapper';
import { ListColumn } from '../../components/lists/ListColumn';
import { ListHeaderTitle } from '../../components/lists/ListHeaderTitle';
import { InlineValue } from '../../components/InlineValue';
import { EPositionType, IEnrichedUniswapPosition } from '../../types/uniswapTokens';
import { ListItem } from '../../components/lists/ListItem';
import { ListValueColumn } from '../dashboard/lists/ListValueColumn';
import { TokenIcon } from '../../components/primitives/TokenIcon';

const head = [
  {
    title: <Trans key='symbol'>Asset</Trans>,
    sortKey: 'symbol',
  },
  {
    title: <Trans key="liquidity">Liquidity</Trans>,
    sortKey: 'variableBorrows',
  },
  {
    title: <Trans key="debtValue">Debt Value</Trans>,
    sortKey: 'debtValue',
    leveraged: true,
  },
  {
    title: <Trans key="feesEarned">Fees Earned</Trans>,
    sortKey: 'feesEarned',
  },
  {
    title: <Trans key="netWorth">Net Worth</Trans>,
    sortKey: 'netWorth',
    leveraged: true,
  },
];

export interface ILeveragePositionInfoProps extends IEnrichedUniswapPosition {}

export const LeveragePositionInfo = (props: ILeveragePositionInfoProps) => {
  const debt0 = props.debtAsset === props.token0.underlyingAsset ? props.debt : 0;
  const debt1 = props.debtAsset === props.token1.underlyingAsset ? props.debt : 0;
  const marketReferenceCurrencyDebt0 = props.debtAsset === props.token0.underlyingAsset ? props.marketReferenceCurrencyDebt : 0;
  const marketReferenceCurrencyDebt1 = props.debtAsset === props.token1.underlyingAsset ? props.marketReferenceCurrencyDebt : 0;
  const currentLeverage = props.marketReferenceCurrencyValue / (
    props.marketReferenceCurrencyValue - (props.marketReferenceCurrencyDebt || 0)
  );
  const leveragedPosition = props.type === EPositionType.LeveragedPosition;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between' }}>
        <Typography variant="h4">
          Position Info
        </Typography>
        <InlineValue label="ID:" value={props.tokenId.toString()} />
      </Box>
      <Box
        sx={{
          my: 2,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <ListHeaderWrapper
          sx={{ backgroundColor: 'transparent', borderTop: 'none' }}
        >
          {head
            .filter((col) => leveragedPosition || (!leveragedPosition && !col.leveraged))
            .map((col) => (
              <ListColumn
                isRow={col.sortKey === 'symbol'}
                maxWidth={col.sortKey === 'symbol' ? DASHBOARD_LIST_COLUMN_WIDTHS.ASSET : undefined}
                key={col.sortKey}
              >
                <ListHeaderTitle>
                  {col.title}
                </ListHeaderTitle>
              </ListColumn>
            ))}
        </ListHeaderWrapper>

        <ListItem>
          <ListColumn maxWidth={DASHBOARD_LIST_COLUMN_WIDTHS.CELL} isRow>
            <TokenIcon symbol={props.token0.iconSymbol} fontSize="large" />
            <Typography variant="subheader1" sx={{ ml: 3 }} noWrap data-cy={`assetName`}>
              {props.token0.symbol}
            </Typography>
          </ListColumn>
          <ListValueColumn
            value={props.token0.liquidity}
            subValue={props.token0.marketReferenceCurrencyValue}
            symbol={props.token0.iconSymbol}
          />
          {leveragedPosition && (
            <ListValueColumn
              value={debt0 || 0}
              subValue={marketReferenceCurrencyDebt0 || 0}
              symbol={props.token0.iconSymbol}
            />
          )}
          <ListValueColumn
            value={props.token0.fee}
            subValue={props.token0.marketReferenceCurrencyFee}
            symbol={props.token0.iconSymbol}
          />
          {leveragedPosition && (
            <ListValueColumn
              value={props.token0.netWorth}
              subValue={props.token0.netWorthUsd}
              symbol={props.token0.iconSymbol}
            />
          )}
        </ListItem>

        <ListItem>
          <ListColumn maxWidth={DASHBOARD_LIST_COLUMN_WIDTHS.CELL} isRow>
            <TokenIcon symbol={props.token1.iconSymbol} fontSize="large" />
            <Typography variant="subheader1" sx={{ ml: 3 }} noWrap data-cy={`assetName`}>
              {props.token1.symbol}
            </Typography>
          </ListColumn>
          <ListValueColumn
            value={props.token1.liquidity}
            subValue={props.token1.marketReferenceCurrencyValue}
            symbol={props.token1.iconSymbol}
          />
          {leveragedPosition && (
            <ListValueColumn
              value={debt1 || 0}
              subValue={marketReferenceCurrencyDebt1 || 0}
              symbol={props.token1.iconSymbol}
            />
          )}
          <ListValueColumn
            value={props.token1.fee}
            subValue={props.token1.marketReferenceCurrencyFee}
            symbol={props.token1.iconSymbol}
          />
          {leveragedPosition && (
            <ListValueColumn
              value={props.token1.netWorth}
              subValue={props.token1.netWorthUsd}
              symbol={props.token1.iconSymbol}
            />
          )}
        </ListItem>
      </Box>
      {leveragedPosition && (
        <InlineValue label="Current Leverage:" value={currentLeverage.toFixed(2)} />
      )}
    </Box>
  );
};
